/**
 * Pin Type Utilities for ERC Validation
 *
 * This module provides utilities for working with electrical pin types,
 * including mapping from Flux pin type strings to ERC pin types and
 * the pin-to-pin compatibility matrix based on KiCad's implementation.
 *
 * @see KiCad's eeschema/erc/erc_settings.cpp for the original matrix
 */
import { ElectricalPinType, ErcSeverity } from "./types";
// Re-export ElectricalPinType for use by DRC validators
export { ElectricalPinType };
/**
 * Maps Flux pin type option strings to ERC electrical pin types.
 * These strings come from the pinTypeOptions constant in @buildwithflux/constants.
 */
const FLUX_PIN_TYPE_MAP = {
    "Bi-Directional": ElectricalPinType.BIDIRECTIONAL,
    Digital: ElectricalPinType.UNSPECIFIED,
    Ground: ElectricalPinType.POWER_IN,
    Input: ElectricalPinType.INPUT,
    "Not Connected": ElectricalPinType.NO_CONNECT,
    "Open Collector": ElectricalPinType.OPEN_COLLECTOR,
    "Open Emitter": ElectricalPinType.OPEN_EMITTER,
    Output: ElectricalPinType.OUTPUT,
    Passive: ElectricalPinType.PASSIVE,
    "Power In": ElectricalPinType.POWER_IN,
    "Power Out": ElectricalPinType.POWER_OUT,
    Programmable: ElectricalPinType.UNSPECIFIED,
    Reserved: ElectricalPinType.UNSPECIFIED,
    Signal: ElectricalPinType.UNSPECIFIED,
    "Tri-State": ElectricalPinType.TRISTATE,
    Unspecified: ElectricalPinType.UNSPECIFIED,
};
/**
 * Converts a Flux pin type string to an ERC electrical pin type.
 *
 * @param fluxPinType - The Flux pin type string (from pinTypeOptions)
 * @returns The corresponding ERC electrical pin type
 */
export function fluxPinTypeToElectricalType(fluxPinType) {
    if (!fluxPinType) {
        return ElectricalPinType.UNSPECIFIED;
    }
    return FLUX_PIN_TYPE_MAP[fluxPinType] ?? ElectricalPinType.UNSPECIFIED;
}
/**
 * Pin-to-pin compatibility matrix based on KiCad's ERC implementation.
 *
 * This matrix defines the severity of connecting two pins of different types.
 * The matrix is symmetric, so pinMatrix[A][B] === pinMatrix[B][A].
 *
 * Legend:
 * - OK: Connection is valid
 * - WARNING: Connection may be problematic but could be intentional
 * - ERROR: Connection is definitely wrong
 *
 * @see KiCad's eeschema/erc/erc_settings.cpp - default_pin_to_pin_flags
 */
const PIN_COMPATIBILITY_MATRIX = {
    [ElectricalPinType.INPUT]: {
        [ElectricalPinType.INPUT]: ErcSeverity.OK,
        [ElectricalPinType.OUTPUT]: ErcSeverity.OK,
        [ElectricalPinType.BIDIRECTIONAL]: ErcSeverity.OK,
        [ElectricalPinType.TRISTATE]: ErcSeverity.OK,
        [ElectricalPinType.PASSIVE]: ErcSeverity.OK,
        [ElectricalPinType.UNSPECIFIED]: ErcSeverity.OK,
        [ElectricalPinType.POWER_IN]: ErcSeverity.OK,
        [ElectricalPinType.POWER_OUT]: ErcSeverity.OK,
        [ElectricalPinType.OPEN_COLLECTOR]: ErcSeverity.OK,
        [ElectricalPinType.OPEN_EMITTER]: ErcSeverity.OK,
        [ElectricalPinType.NO_CONNECT]: ErcSeverity.ERROR,
    },
    [ElectricalPinType.OUTPUT]: {
        [ElectricalPinType.INPUT]: ErcSeverity.OK,
        [ElectricalPinType.OUTPUT]: ErcSeverity.ERROR, // Two outputs connected
        [ElectricalPinType.BIDIRECTIONAL]: ErcSeverity.WARNING,
        [ElectricalPinType.TRISTATE]: ErcSeverity.WARNING,
        [ElectricalPinType.PASSIVE]: ErcSeverity.OK,
        [ElectricalPinType.UNSPECIFIED]: ErcSeverity.OK,
        [ElectricalPinType.POWER_IN]: ErcSeverity.OK,
        [ElectricalPinType.POWER_OUT]: ErcSeverity.ERROR, // Output to power output
        [ElectricalPinType.OPEN_COLLECTOR]: ErcSeverity.ERROR,
        [ElectricalPinType.OPEN_EMITTER]: ErcSeverity.ERROR,
        [ElectricalPinType.NO_CONNECT]: ErcSeverity.ERROR,
    },
    [ElectricalPinType.BIDIRECTIONAL]: {
        [ElectricalPinType.INPUT]: ErcSeverity.OK,
        [ElectricalPinType.OUTPUT]: ErcSeverity.WARNING,
        [ElectricalPinType.BIDIRECTIONAL]: ErcSeverity.OK,
        [ElectricalPinType.TRISTATE]: ErcSeverity.OK,
        [ElectricalPinType.PASSIVE]: ErcSeverity.OK,
        [ElectricalPinType.UNSPECIFIED]: ErcSeverity.OK,
        [ElectricalPinType.POWER_IN]: ErcSeverity.WARNING,
        [ElectricalPinType.POWER_OUT]: ErcSeverity.WARNING,
        [ElectricalPinType.OPEN_COLLECTOR]: ErcSeverity.WARNING,
        [ElectricalPinType.OPEN_EMITTER]: ErcSeverity.WARNING,
        [ElectricalPinType.NO_CONNECT]: ErcSeverity.ERROR,
    },
    [ElectricalPinType.TRISTATE]: {
        [ElectricalPinType.INPUT]: ErcSeverity.OK,
        [ElectricalPinType.OUTPUT]: ErcSeverity.WARNING,
        [ElectricalPinType.BIDIRECTIONAL]: ErcSeverity.OK,
        [ElectricalPinType.TRISTATE]: ErcSeverity.OK,
        [ElectricalPinType.PASSIVE]: ErcSeverity.OK,
        [ElectricalPinType.UNSPECIFIED]: ErcSeverity.OK,
        [ElectricalPinType.POWER_IN]: ErcSeverity.WARNING,
        [ElectricalPinType.POWER_OUT]: ErcSeverity.WARNING,
        [ElectricalPinType.OPEN_COLLECTOR]: ErcSeverity.OK,
        [ElectricalPinType.OPEN_EMITTER]: ErcSeverity.OK,
        [ElectricalPinType.NO_CONNECT]: ErcSeverity.ERROR,
    },
    [ElectricalPinType.PASSIVE]: {
        [ElectricalPinType.INPUT]: ErcSeverity.OK,
        [ElectricalPinType.OUTPUT]: ErcSeverity.OK,
        [ElectricalPinType.BIDIRECTIONAL]: ErcSeverity.OK,
        [ElectricalPinType.TRISTATE]: ErcSeverity.OK,
        [ElectricalPinType.PASSIVE]: ErcSeverity.OK,
        [ElectricalPinType.UNSPECIFIED]: ErcSeverity.OK,
        [ElectricalPinType.POWER_IN]: ErcSeverity.OK,
        [ElectricalPinType.POWER_OUT]: ErcSeverity.OK,
        [ElectricalPinType.OPEN_COLLECTOR]: ErcSeverity.OK,
        [ElectricalPinType.OPEN_EMITTER]: ErcSeverity.OK,
        [ElectricalPinType.NO_CONNECT]: ErcSeverity.ERROR,
    },
    [ElectricalPinType.UNSPECIFIED]: {
        [ElectricalPinType.INPUT]: ErcSeverity.OK,
        [ElectricalPinType.OUTPUT]: ErcSeverity.OK,
        [ElectricalPinType.BIDIRECTIONAL]: ErcSeverity.OK,
        [ElectricalPinType.TRISTATE]: ErcSeverity.OK,
        [ElectricalPinType.PASSIVE]: ErcSeverity.OK,
        [ElectricalPinType.UNSPECIFIED]: ErcSeverity.OK,
        [ElectricalPinType.POWER_IN]: ErcSeverity.OK,
        [ElectricalPinType.POWER_OUT]: ErcSeverity.OK,
        [ElectricalPinType.OPEN_COLLECTOR]: ErcSeverity.OK,
        [ElectricalPinType.OPEN_EMITTER]: ErcSeverity.OK,
        [ElectricalPinType.NO_CONNECT]: ErcSeverity.ERROR,
    },
    [ElectricalPinType.POWER_IN]: {
        [ElectricalPinType.INPUT]: ErcSeverity.OK,
        [ElectricalPinType.OUTPUT]: ErcSeverity.OK,
        [ElectricalPinType.BIDIRECTIONAL]: ErcSeverity.WARNING,
        [ElectricalPinType.TRISTATE]: ErcSeverity.WARNING,
        [ElectricalPinType.PASSIVE]: ErcSeverity.OK,
        [ElectricalPinType.UNSPECIFIED]: ErcSeverity.OK,
        [ElectricalPinType.POWER_IN]: ErcSeverity.OK, // Multiple power inputs on same net is OK
        [ElectricalPinType.POWER_OUT]: ErcSeverity.OK,
        [ElectricalPinType.OPEN_COLLECTOR]: ErcSeverity.WARNING,
        [ElectricalPinType.OPEN_EMITTER]: ErcSeverity.WARNING,
        [ElectricalPinType.NO_CONNECT]: ErcSeverity.ERROR,
    },
    [ElectricalPinType.POWER_OUT]: {
        [ElectricalPinType.INPUT]: ErcSeverity.OK,
        [ElectricalPinType.OUTPUT]: ErcSeverity.ERROR,
        [ElectricalPinType.BIDIRECTIONAL]: ErcSeverity.WARNING,
        [ElectricalPinType.TRISTATE]: ErcSeverity.WARNING,
        [ElectricalPinType.PASSIVE]: ErcSeverity.OK,
        [ElectricalPinType.UNSPECIFIED]: ErcSeverity.OK,
        [ElectricalPinType.POWER_IN]: ErcSeverity.OK,
        [ElectricalPinType.POWER_OUT]: ErcSeverity.ERROR, // Two power outputs connected
        [ElectricalPinType.OPEN_COLLECTOR]: ErcSeverity.ERROR,
        [ElectricalPinType.OPEN_EMITTER]: ErcSeverity.ERROR,
        [ElectricalPinType.NO_CONNECT]: ErcSeverity.ERROR,
    },
    [ElectricalPinType.OPEN_COLLECTOR]: {
        [ElectricalPinType.INPUT]: ErcSeverity.OK,
        [ElectricalPinType.OUTPUT]: ErcSeverity.ERROR,
        [ElectricalPinType.BIDIRECTIONAL]: ErcSeverity.WARNING,
        [ElectricalPinType.TRISTATE]: ErcSeverity.OK,
        [ElectricalPinType.PASSIVE]: ErcSeverity.OK,
        [ElectricalPinType.UNSPECIFIED]: ErcSeverity.OK,
        [ElectricalPinType.POWER_IN]: ErcSeverity.WARNING,
        [ElectricalPinType.POWER_OUT]: ErcSeverity.ERROR,
        [ElectricalPinType.OPEN_COLLECTOR]: ErcSeverity.OK, // Wired-AND is valid
        [ElectricalPinType.OPEN_EMITTER]: ErcSeverity.OK,
        [ElectricalPinType.NO_CONNECT]: ErcSeverity.ERROR,
    },
    [ElectricalPinType.OPEN_EMITTER]: {
        [ElectricalPinType.INPUT]: ErcSeverity.OK,
        [ElectricalPinType.OUTPUT]: ErcSeverity.ERROR,
        [ElectricalPinType.BIDIRECTIONAL]: ErcSeverity.WARNING,
        [ElectricalPinType.TRISTATE]: ErcSeverity.OK,
        [ElectricalPinType.PASSIVE]: ErcSeverity.OK,
        [ElectricalPinType.UNSPECIFIED]: ErcSeverity.OK,
        [ElectricalPinType.POWER_IN]: ErcSeverity.WARNING,
        [ElectricalPinType.POWER_OUT]: ErcSeverity.ERROR,
        [ElectricalPinType.OPEN_COLLECTOR]: ErcSeverity.OK,
        [ElectricalPinType.OPEN_EMITTER]: ErcSeverity.OK, // Wired-OR is valid
        [ElectricalPinType.NO_CONNECT]: ErcSeverity.ERROR,
    },
    [ElectricalPinType.NO_CONNECT]: {
        [ElectricalPinType.INPUT]: ErcSeverity.ERROR,
        [ElectricalPinType.OUTPUT]: ErcSeverity.ERROR,
        [ElectricalPinType.BIDIRECTIONAL]: ErcSeverity.ERROR,
        [ElectricalPinType.TRISTATE]: ErcSeverity.ERROR,
        [ElectricalPinType.PASSIVE]: ErcSeverity.ERROR,
        [ElectricalPinType.UNSPECIFIED]: ErcSeverity.ERROR,
        [ElectricalPinType.POWER_IN]: ErcSeverity.ERROR,
        [ElectricalPinType.POWER_OUT]: ErcSeverity.ERROR,
        [ElectricalPinType.OPEN_COLLECTOR]: ErcSeverity.ERROR,
        [ElectricalPinType.OPEN_EMITTER]: ErcSeverity.ERROR,
        [ElectricalPinType.NO_CONNECT]: ErcSeverity.OK, // NC to NC is OK (shouldn't happen)
    },
};
/**
 * Gets the ERC severity for connecting two pins of the given types.
 *
 * @param type1 - The electrical type of the first pin
 * @param type2 - The electrical type of the second pin
 * @returns The severity of connecting these two pin types
 */
export function getPinCompatibility(type1, type2) {
    return PIN_COMPATIBILITY_MATRIX[type1][type2];
}
/**
 * Checks if a pin type is a driver (can drive a net).
 * Drivers include outputs, power outputs, bidirectional, tristate, and passive pins.
 *
 * @param pinType - The electrical pin type to check
 * @returns True if the pin type can drive a net
 */
export function isPinDriver(pinType) {
    return (pinType === ElectricalPinType.OUTPUT ||
        pinType === ElectricalPinType.POWER_OUT ||
        pinType === ElectricalPinType.BIDIRECTIONAL ||
        pinType === ElectricalPinType.TRISTATE ||
        pinType === ElectricalPinType.PASSIVE ||
        pinType === ElectricalPinType.OPEN_COLLECTOR ||
        pinType === ElectricalPinType.OPEN_EMITTER);
}
/**
 * Checks if a pin type is a strong driver (definitely drives a net).
 * Strong drivers include outputs and power outputs.
 *
 * @param pinType - The electrical pin type to check
 * @returns True if the pin type is a strong driver
 */
export function isStrongDriver(pinType) {
    return pinType === ElectricalPinType.OUTPUT || pinType === ElectricalPinType.POWER_OUT;
}
/**
 * Checks if a pin type requires a driver.
 * Input and power input pins require a driver on the net.
 *
 * @param pinType - The electrical pin type to check
 * @returns True if the pin type requires a driver
 */
export function pinRequiresDriver(pinType) {
    return pinType === ElectricalPinType.INPUT || pinType === ElectricalPinType.POWER_IN;
}
/**
 * Checks if a pin type is a power pin (power input or power output).
 *
 * @param pinType - The electrical pin type to check
 * @returns True if the pin type is a power pin
 */
export function isPowerPin(pinType) {
    return pinType === ElectricalPinType.POWER_IN || pinType === ElectricalPinType.POWER_OUT;
}
/**
 * Gets a human-readable name for an electrical pin type.
 *
 * @param pinType - The electrical pin type
 * @returns A human-readable name for the pin type
 */
export function getPinTypeName(pinType) {
    const names = {
        [ElectricalPinType.INPUT]: "Input",
        [ElectricalPinType.OUTPUT]: "Output",
        [ElectricalPinType.BIDIRECTIONAL]: "Bidirectional",
        [ElectricalPinType.TRISTATE]: "Tri-State",
        [ElectricalPinType.PASSIVE]: "Passive",
        [ElectricalPinType.UNSPECIFIED]: "Unspecified",
        [ElectricalPinType.POWER_IN]: "Power Input",
        [ElectricalPinType.POWER_OUT]: "Power Output",
        [ElectricalPinType.OPEN_COLLECTOR]: "Open Collector",
        [ElectricalPinType.OPEN_EMITTER]: "Open Emitter",
        [ElectricalPinType.NO_CONNECT]: "Not Connected",
    };
    return names[pinType];
}
//# sourceMappingURL=pinTypes.js.map